import { useEffect, useRef, useState, useCallback } from "react";
import axios from "../../../config/axios";
import useBeaconControl from "../../../hooks/useBeaconControl";
import "./Interact.css";
import InteractTimer from "./InteractTimer";
import InteractAlerts from "./InteractAlerts";
import { LuVolume1, LuVolume2, LuVolumeOff } from "react-icons/lu";

// ---- Assets ----
const BASE_SRC = "/static/interactoff.png"; // 1280x800, both rings dark gray = off
const CAST_WELCOME_SRC = "/static/castmember-welcome.png";
const CAST_MAGICAL_SRC = "/static/castmember-magical.png";
const CAST_SAD_SRC     = "/static/castmember-sad.png";
const CAST_POINT_SRC   = "/static/castmember-point.png";
const CAST_ONE_SRC     = "/static/castmember-one.png"; // finger up: "one second"
const CHIME_SRC        = "/static/ball-chime.mp3";

// Module-level so they're created once and reused; loaded lazily on first play.
const CHIME_VOLUME        = 0.4;  // 0..1
let chimeAudio = null;
const playResultSound = () => {
  try {
    if (!chimeAudio) chimeAudio = new Audio(CHIME_SRC);
    chimeAudio.volume = CHIME_VOLUME * effVol();
    chimeAudio.currentTime = 0;
    chimeAudio.play().catch((e) => console.log("[Interact] sound blocked:", e?.name));
  } catch (e) {
    console.log("[Interact] sound failed:", e);
  }
};

// ---- Cast member voice lines (pregenerated, /static). Key → file. ----
const VOICE_VOLUME = 0.9;
const VOICE_SRC = {
  menu:     "/static/voice-menu.mp3",
  hi:       "/static/voice-hi.mp3",
  dvcask:   "/static/voice-dvcask.mp3",
  tap:      "/static/voice-tap.mp3",
  dormant:  "/static/voice-dormant.mp3",
  herewego: "/static/voice-herewego.mp3",
  finger:   "/static/voice-finger.mp3",
  detected: "/static/voice-detected.mp3",
  keep:     "/static/voice-keep.mp3",
  nudge:    "/static/voice-nudge.mp3",
  magical:  "/static/voice-magical.mp3",
  welcomehome: "/static/voice-welcomehome.mp3",
  pointer:  "/static/voice-pointer.mp3",
  manager:  "/static/voice-manager.mp3",
};
const voiceCache = {};
let currentVoice = null;
const stopVoice = () => {
  try { if (currentVoice) { currentVoice.pause(); currentVoice = null; } } catch {}
};
const playVoice = (key) => {
  try {
    stopVoice();
    if (!VOICE_SRC[key]) return;
    if (!voiceCache[key]) voiceCache[key] = new Audio(VOICE_SRC[key]);
    const a = voiceCache[key];
    a.volume = VOICE_VOLUME * effVol();
    a.currentTime = 0;
    currentVoice = a;
    a.play().catch((e) => console.log("[Interact] voice blocked:", e?.name));
  } catch (e) {
    console.log("[Interact] voice failed:", e);
  }
};

// ---- Page volume: scales her voice + the chime. Persisted. ----
let ixVol = 1;
let ixMuted = false;
try {
  const v = parseFloat(localStorage.getItem("waitsigns_ix_vol"));
  if (!Number.isNaN(v)) ixVol = Math.min(1, Math.max(0, v));
  ixMuted = localStorage.getItem("waitsigns_ix_muted") === "1";
} catch {}
const effVol = () => (ixMuted ? 0 : ixVol);
const applyVolumesNow = () => {
  try { if (currentVoice) currentVoice.volume = VOICE_VOLUME * effVol(); } catch {}
  try { if (chimeAudio) chimeAudio.volume = CHIME_VOLUME * effVol(); } catch {}
};
const setIxVol = (v) => {
  ixVol = Math.min(1, Math.max(0, v));
  try { localStorage.setItem("waitsigns_ix_vol", String(ixVol)); } catch {}
  applyVolumesNow();
};
const setIxMuted = (m) => {
  ixMuted = m;
  try { localStorage.setItem("waitsigns_ix_muted", m ? "1" : "0"); } catch {}
  applyVolumesNow();
};

// Volume trio styling — shared by the menu stage and the scanner screen.
const IXV_STYLE = `
  .ix-menu-stage{position:relative;}
  .ixv-controls{position:absolute;left:18px;bottom:18px;z-index:5;display:flex;flex-direction:column;gap:14px;}
  .ixv-btn{width:72px;height:72px;border-radius:50%;border:none;background:rgba(26,37,66,.85);
    color:#fff;display:flex;align-items:center;justify-content:center;cursor:pointer;outline:none;
    transition:transform .1s ease;}
  .ixv-btn:active{transform:scale(.94);}
  .ixv-btn.ixv-muted{background:#e5484d;}
`;

// ---- Colors. Tweak freely. ----
const WHITE = "#ffffff";
const GREEN = "#3dff1a";
const BLUE  = "#5ac8ff";
const PURPLE = "#bf5af2";

// ---- Messages ----
const MSG_TAP        = "Go ahead and tap the scanner!";
const MSG_DORMANT    = "Tap the scanner when you're ready!";
const MSG_HERE_WE_GO = 'Great, get ready to "scan" your finger...';
const MSG_FINGER     = "Place your finger on the scanner";
const MSG_DETECTED   = "Perfect! Hold it right there...";
const MSG_KEEP       = "Keep holding...";
const MSG_NUDGE      = "Hmm...I'm not detecting anything. Move your finger around a bit!";
const MSG_GREEN      = "Have a Magical Day!";
const MSG_DVC_HOME   = "Welcome Home!";
const MSG_POINTER    = "Your pointer finger will work best...";
const MSG_BLUE       = "Let me grab my manager…";
const MSG_DVC_ASK    = "Are you DVC Members?";

// ---- Geometry, measured from interactoff.png (1280x800). Don't touch unless the art changes. ----
const GLOBE = { cx: 452.5, cy: 292, rx: 153, ry: 150, width: 19 };
const SCANNER = { cx: 889, cy: 468.5, rxOut: 124, ryOut: 63.5, rxIn: 113, ryIn: 59, gapStart: 71, gapEnd: 107 };
const ARROW_POS = { x: 889, y: 345 }; // bouncing arrow tip sits above the scanner ring

const annulusPath = (cx, cy, rxOut, ryOut, rxIn, ryIn) =>
  `M ${cx - rxOut} ${cy} a ${rxOut} ${ryOut} 0 1 0 ${rxOut * 2} 0 a ${rxOut} ${ryOut} 0 1 0 ${-rxOut * 2} 0 Z ` +
  `M ${cx - rxIn} ${cy} a ${rxIn} ${ryIn} 0 1 0 ${rxIn * 2} 0 a ${rxIn} ${ryIn} 0 1 0 ${-rxIn * 2} 0 Z`;
const { cx: SX, cy: SY } = SCANNER;
const SCANNER_CORE_PATH = annulusPath(SX, SY, SCANNER.rxOut, SCANNER.ryOut, SCANNER.rxIn, SCANNER.ryIn);
const SCANNER_GLOW_PATH = annulusPath(SX, SY, SCANNER.rxOut + 10, SCANNER.ryOut + 7, SCANNER.rxIn - 9, SCANNER.ryIn - 6);
const SCANNER_HOT_PATH  = annulusPath(SX, SY, SCANNER.rxOut - 2, SCANNER.ryOut - 1.5, SCANNER.rxIn + 2, SCANNER.ryIn + 1);
const gapPoint = (deg) => {
  const t = (deg * Math.PI) / 180;
  return `${SX + 3 * SCANNER.rxOut * Math.cos(t)} ${SY + 3 * SCANNER.ryOut * Math.sin(t)}`;
};
const SCANNER_GAP_CLIP =
  `M 0 0 H 1280 V 800 H 0 Z M ${SX} ${SY} L ${gapPoint(SCANNER.gapStart)} L ${gapPoint(SCANNER.gapEnd)} Z`;

// ---- Screen timing (ms). Tweak freely. ----
const STANDBY_LAP_MS   = 3000;  // standby sweep: one lap
const SCAN_LAP_MS      = 600;   // fast sweep: one lap (runs from CIRCLE all the way through the finger prompt)
const FLASH_CYCLE_MS   = 1200;  // one 100 → 0 → 100 flash
const DISSOLVE_MS      = 600;   // color fade-in / fade-out
const HOLD_REQUIRED_MS = 4000;  // finger must stay down this long → green
const FINGER_WINDOW_MS = 10000; // from the arrow appearing; no held finger by then → blue (a hold in progress is allowed to finish)
const NUDGE_AFTER_MS   = 4000;  // no finger by then → "move your finger around" line
const KEEP_HOLDING_AFTER_MS = 4000; // touch + this long → "Keep holding..." replaces "Perfect!" (runs past hold completion into the resolve wait)
const POINTER_HINT_AFTER_MS = 6000; // tap + this long → "pointer finger" line takes over from "get ready to scan"

// ---- Band timing (ms). Everything is a 2s gap unless noted. ----
const GAP_MS              = 2000;
const STANDBY_FAST_MS     = 2000;  // standby: FAST every N
const ARM_AFTER_WAKEUPS   = 3;     // tap allowed after this many real FAST sends (breathing room so bands are truly awake)
const STANDBY_WAKE_MAX_MS = 60000; // standby FAST loop cap — after this with no tap, CANCEL + dormant (no radio) until tapped
const CIRCLE_REPEAT_MS    = 3000;  // CIRCLE → 3s → CIRCLE again → 3s → CANCEL
const PRELUDE_FAST_COUNT  = 2;     // FASTs after CIRCLE before we're allowed to resolve
const RESULT_HOLD_MS      = 5000;  // SOLID color held on screen this long, then CANCEL
const SOLID_OFF_HOLD_MS   = 5000;  // SOLID_OFF runs this long before standby's wakeups start
const BAND_COLOR_LEAD_MS  = 1500;  // band gets the color command this long BEFORE the screen shows it (field-tuned: 2000 had bands ~1s early)

/* ---- Raw transport — same endpoints as BeaconDemo / MagicBand ---- */
const postShowCmd = (deviceId, fx, vib) =>
  axios
    .post("/esp/beacon-show", { deviceId, fx, vib }, { withCredentials: true })
    .catch((err) => console.error("[Interact] cmd failed:", fx, err));
const postCancelRaw = (deviceId) =>
  axios
    .post("/esp/cancel", { deviceId }, { withCredentials: true })
    .catch((err) => console.error("[Interact] cancel failed:", err));

// Screen phases:
// standby  → white sweep; band CANCEL → 2s → FAST every 2s; armed after 3 wakeups (or instantly after a cycle); dormant after 60s idle
//            first-ever visit: DVC yes/no question when armed (before the tap prompt)
// welcome  → tap: wait until 2s after the last command → CANCEL → 2s → CIRCLE
// scan     → CIRCLE ×2 on band; globe fast sweep (fast sweep then continues through ready + prompt)
// ready    → CANCEL after circles
// prompt   → first FAST: scanner ring flashing, arrow, finger window open
// green / blue       → band got SOLID color 1.5s ago; screen takes the color, held RESULT_HOLD_MS
// greenOut / blueOut → CANCEL fired; everything fades; SOLID_OFF runs SOLID_OFF_HOLD_MS → standby (already armed)

const GlobeLayers = ({ sweep, lapMs }) => {
  const geo = { cx: GLOBE.cx, cy: GLOBE.cy, rx: GLOBE.rx, ry: GLOBE.ry };
  const sw = sweep ? { pathLength: "100", style: { animationDuration: `${lapMs}ms` } } : {};
  const cls = sweep ? " ix-sweep" : "";
  return (
    <>
      <ellipse className={`ix-glow-outer${cls}`} {...geo} {...sw} strokeWidth={GLOBE.width * 3.6} />
      <ellipse className={`ix-glow${cls}`}       {...geo} {...sw} strokeWidth={GLOBE.width * 2} />
      <ellipse className={`ix-core${cls}`}       {...geo} {...sw} strokeWidth={GLOBE.width} />
      <ellipse className={`ix-hot${cls}`}        {...geo} {...sw} strokeWidth={GLOBE.width * 0.4} />
    </>
  );
};

const ScannerLayers = () => (
  <>
    <path className="ix-glow" d={SCANNER_GLOW_PATH} fillRule="evenodd" clipPath="url(#ix-scanner-gap)" />
    <path className="ix-core" d={SCANNER_CORE_PATH} fillRule="evenodd" clipPath="url(#ix-scanner-gap)" />
    <path className="ix-hot"  d={SCANNER_HOT_PATH}  fillRule="evenodd" clipPath="url(#ix-scanner-gap)" />
  </>
);

const Interact = ({ onBack, device }) => {
  const [screen, setScreen] = useState("menu"); // "menu" | "scanner"
  const screenRef = useRef("menu");
  useEffect(() => { screenRef.current = screen; }, [screen]);
  const [phase, setPhase] = useState("standby");
  const [armed, setArmed] = useState(false);
  const [dormant, setDormant] = useState(false); // standby wake cap hit; screen alive, radio quiet
  const [fingerDown, setFingerDown] = useState(false);
  const [longHold, setLongHold] = useState(false); // held past KEEP_HOLDING_AFTER_MS
  const [pendingControl, setPendingControl] = useState(false); // take-control modal up
  const [dvc, setDvc] = useState(() => {
    try { return localStorage.getItem("waitsigns_ix_dvc") === "1"; } catch { return false; }
  });
  const [dvcKnown, setDvcKnown] = useState(() => {
    try { return localStorage.getItem("waitsigns_ix_dvc") !== null; } catch { return false; }
  });
  const dvcRef = useRef(dvc);
  useEffect(() => { dvcRef.current = dvc; }, [dvc]);
  const toggleDvc = () => {
    setDvc((v) => {
      const next = !v;
      try { localStorage.setItem("waitsigns_ix_dvc", next ? "1" : "0"); } catch {}
      return next;
    });
    setDvcKnown(true);
  };
  const chooseDvc = (isDvc) => {
    setDvc(isDvc);
    try { localStorage.setItem("waitsigns_ix_dvc", isDvc ? "1" : "0"); } catch {}
    setDvcKnown(true);
  };

  const lastName = device?.subscription?.last_name || null;

  /* ---- Beacon control + gated senders (BeaconDemo pattern) ---- */
  const { isController, hasHolder, holder, claim } = useBeaconControl(); 
  const isControllerRef = useRef(isController);
  useEffect(() => { isControllerRef.current = isController; }, [isController]);

  const deviceId = device?.magic_beacon_device?._id ?? null;
  const deviceIdRef = useRef(deviceId);
  useEffect(() => { deviceIdRef.current = deviceId; }, [deviceId]);

  const canSend = useCallback(() => !!deviceIdRef.current && !!isControllerRef.current, []);

  const sendCmd = useCallback((fx, vib) => {
    if (!canSend()) {
      console.log(`[Interact] SKIPPED fx=${fx} (deviceId=${deviceIdRef.current ? "ok" : "none"}, controller=${isControllerRef.current})`);
      return Promise.resolve();
    }
    console.log(`[Interact] send fx=${fx} vib=${vib}`);
    lastCmdAtRef.current = Date.now();
    return postShowCmd(deviceIdRef.current, fx, vib);
  }, [canSend]);
  const sendCancel = useCallback(() => {
    if (!canSend()) {
      console.log(`[Interact] SKIPPED CANCEL (deviceId=${deviceIdRef.current ? "ok" : "none"}, controller=${isControllerRef.current})`);
      return Promise.resolve();
    }
    console.log("[Interact] send CANCEL");
    lastCmdAtRef.current = Date.now();
    return postCancelRaw(deviceIdRef.current);
  }, [canSend]);

  /* ---- Sequencer ---- */
  const timersRef = useRef([]);
  const loopRef = useRef(null);
  const standbyTimerRef = useRef(null);
  const dormantTimerRef = useRef(null);
  const lastFastAtRef = useRef(0);   // last WAKE_UP_FAST (resolve pacing)
  const lastCmdAtRef  = useRef(0);   // last send of any kind (tap handoff pacing)
  const wakeCountRef = useRef(0);
  const rearmRef = useRef(false); // true when standby follows a completed cycle: skip the greeting, start armed
  const preludeDoneRef = useRef(false);
  const resultRef = useRef(null);
  const windowTimerRef = useRef(null);
  const holdTimerRef = useRef(null);
  const nudgeTimerRef = useRef(null);
  const longHoldTimerRef = useRef(null);
  const [nudge, setNudge] = useState(false);
  const [pointerHint, setPointerHint] = useState(false); // 5s after tap: "pointer finger" line
  const fingerDownRef = useRef(false);     // timer callbacks can't read state
  const windowExpiredRef = useRef(false);  // 10s ran out while a hold was in progress

  const after = useCallback((ms, fn) => {
    const t = setTimeout(fn, ms);
    timersRef.current.push(t);
    return t;
  }, []);
  const stopLoop = useCallback(() => {
    if (loopRef.current) { clearInterval(loopRef.current); loopRef.current = null; }
    clearTimeout(standbyTimerRef.current);
    clearTimeout(dormantTimerRef.current);
  }, []);
  const clearAll = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
    stopLoop();
    clearTimeout(windowTimerRef.current);
    clearTimeout(holdTimerRef.current);
    clearTimeout(nudgeTimerRef.current);
    clearTimeout(longHoldTimerRef.current);
  }, [stopLoop]);

  // Unmount = user left via the bottom bar / anywhere outside Interact:
  // CANCEL → 2s → SOLID_OFF, same as Back. Refs survive unmount; the timeout
  // is untracked on purpose so SOLID_OFF completes.
  useEffect(() => () => {
    stopVoice();
    clearAll();
    if (screenRef.current === "scanner") {
      sendCancel();
      setTimeout(() => sendCmd("SOLID_OFF", "OFF"), GAP_MS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // First-ever visit: ask the DVC question where "tap the scanner" would appear.
  const asking = screen === "scanner" && phase === "standby" && armed && !dormant && !dvcKnown;

  /* ---- Voice: mirrors the bubble. One line per state change; new line cuts the old. ---- */
    const voiceKey =
    screen === "menu" ? "menu" :
    screen === "timer" ? null : // timer loads silent; null also cuts any playing line
    screen === "alerts" ? null : // alerts too
    phase === "standby" ? (dormant ? "dormant" : asking ? "dvcask" : armed ? "tap" : "hi") :
    phase === "welcome" || phase === "scan" || phase === "ready" ? (pointerHint ? "pointer" : "herewego") :
    phase === "prompt"
      ? (fingerDown || resultRef.current
          ? (longHold ? "keep" : "detected")
          : nudge ? "nudge" : "finger")
      : phase === "green" ? (dvcRef.current ? "welcomehome" : "magical") :
    phase === "blue" ? "manager" : null;

  useEffect(() => {
    if (!voiceKey) { stopVoice(); return; }
    if (voiceKey === "menu") {
      const t = setTimeout(() => playVoice("menu"), 400);
      return () => clearTimeout(t);
    }
    if (voiceKey === "magical" || voiceKey === "welcomehome") {
      const t = setTimeout(() => playVoice(voiceKey), 600); // chime first, then her
      return () => clearTimeout(t);
    }
    playVoice(voiceKey);
  }, [voiceKey]);

  const fireFast = useCallback(() => {
    lastFastAtRef.current = Date.now();
    if (canSend()) wakeCountRef.current += 1;
    sendCmd("WAKE_UP_FAST", "OFF");
    if (wakeCountRef.current >= ARM_AFTER_WAKEUPS) setArmed(true);
  }, [canSend, sendCmd]);

  const resolve = useCallback((kind) => {
    stopLoop();
    const wait = Math.max(0, lastFastAtRef.current + GAP_MS - Date.now());
    console.log(`[Interact] resolving ${kind}: CANCEL in ${wait}ms`);
    after(wait, () => {
      sendCancel();
      after(GAP_MS, () => {
        const fx = kind === "green" ? (dvcRef.current ? "SOLID_PURPLE" : "SOLID_GREEN") : "SOLID_CYAN";
        sendCmd(fx, "PULSE");
        after(BAND_COLOR_LEAD_MS, () => {
          setPhase(kind); // screen follows the band by the lead time
          if (kind === "green") playResultSound();
          after(RESULT_HOLD_MS, () => {
            sendCancel();
            setPhase(`${kind}Out`);
            after(GAP_MS, () => {
              sendCmd("SOLID_OFF", "OFF");
              rearmRef.current = true; // bands are warm; standby restarts already tappable
              after(SOLID_OFF_HOLD_MS, () => setPhase("standby"));
            });
          });
        });
      });
    });
  }, [after, stopLoop, sendCmd, sendCancel]);

  const settleResult = useCallback((kind) => {
    if (resultRef.current) return;
    resultRef.current = kind;
    clearTimeout(windowTimerRef.current);
    clearTimeout(holdTimerRef.current);
    setFingerDown(false);
    fingerDownRef.current = false;
    console.log(`[Interact] result=${kind} (prelude ${preludeDoneRef.current ? "done" : "still running"})`);
    if (preludeDoneRef.current) resolve(kind);
  }, [resolve]);

  const runPreludeFasts = useCallback((n) => {
    fireFast();
    if (n === 1) setPhase("prompt");
    if (n < PRELUDE_FAST_COUNT) {
      after(GAP_MS, () => runPreludeFasts(n + 1));
      return;
    }
    preludeDoneRef.current = true;
    if (resultRef.current) {
      resolve(resultRef.current);
    } else {
      console.log("[Interact] prelude done, no result yet — FAST every 2s until result");
      loopRef.current = setInterval(fireFast, GAP_MS);
    }
  }, [after, fireFast, resolve]);

  /* ---- Standby: CANCEL → 2s → FAST every 2s; arms after 3 wakeups (or instantly post-cycle); dormant after 60s ---- */
  useEffect(() => {
    if (screen !== "scanner" || phase !== "standby" || dormant) return;
    resultRef.current = null;
    preludeDoneRef.current = false;
    if (rearmRef.current) {
      rearmRef.current = false;
      wakeCountRef.current = ARM_AFTER_WAKEUPS; // fresh off a cycle: no greeting, tap ready now
      setArmed(true);
    } else {
      wakeCountRef.current = 0;
      setArmed(false);
    }
    sendCancel();
    standbyTimerRef.current = setTimeout(() => {
      fireFast();
      loopRef.current = setInterval(fireFast, STANDBY_FAST_MS);
    }, GAP_MS);
    dormantTimerRef.current = setTimeout(() => {
      console.log("[Interact] standby wake cap hit — CANCEL + dormant until tapped");
      stopLoop();
      sendCancel();
      setArmed(false);
      setDormant(true);
    }, STANDBY_WAKE_MAX_MS);
    return stopLoop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, phase, isController, dormant]);

  /* ---- Welcome (tap): wait out the 2s gap from the last command → CANCEL → 2s → CIRCLE ×2 ---- */
  useEffect(() => {
    if (phase !== "welcome") return;
    setPointerHint(false);
    after(POINTER_HINT_AFTER_MS, () => setPointerHint(true));
    const wait = Math.max(0, lastCmdAtRef.current + GAP_MS - Date.now());
    console.log(`[Interact] tap: CANCEL in ${wait}ms (2s gap from last command)`);
    after(wait, () => sendCancel());
    after(wait + GAP_MS, () => {
      sendCmd("CIRCLE", "OFF");
      setPhase("scan");
      after(CIRCLE_REPEAT_MS, () => {
        sendCmd("CIRCLE", "OFF"); // re-broadcast for bands that missed the first
        after(CIRCLE_REPEAT_MS, () => {
          sendCancel();
          setPhase("ready");
          after(GAP_MS, () => runPreludeFasts(1));
        });
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  /* ---- Prompt: finger window ---- */
  useEffect(() => {
    if (phase !== "prompt") return;
    setPointerHint(false);
    setNudge(false);
    windowExpiredRef.current = false;
    windowTimerRef.current = setTimeout(() => {
      if (fingerDownRef.current) {
        // Hold in progress as time ran out — let it finish (green lands in <4s).
        // If they lift instead, handleFingerUp settles blue.
        windowExpiredRef.current = true;
        console.log("[Interact] window expired mid-hold — letting the hold finish");
      } else {
        settleResult("blue");
      }
    }, FINGER_WINDOW_MS);
    nudgeTimerRef.current = setTimeout(() => setNudge(true), NUDGE_AFTER_MS);
    return () => {
      clearTimeout(windowTimerRef.current);
      clearTimeout(holdTimerRef.current);
      clearTimeout(nudgeTimerRef.current);
      clearTimeout(longHoldTimerRef.current);
      setFingerDown(false);
      setNudge(false);
      setLongHold(false);
      fingerDownRef.current = false;
      windowExpiredRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const enterScanner = () => {
    setDormant(false);
    setPhase("standby");
    setScreen("scanner");
  };

  // Mirrors the choose-your-color flow: another device holds control → ask first.
  // Nothing renders and no commands fire until control is settled.
  const openScanner = async () => {
    if (isController) { enterScanner(); return; }
      if (hasHolder && holder?.holderDeviceId !== device?._id) {
      pendingForRef.current = "scanner";
      setPendingControl(true);
      return;
    }
    const r = await claim();
    if (r?.ok) enterScanner();
    else console.error("[Interact] claim failed:", r?.reason);
  };

    const confirmTakeControl = async () => {
    setPendingControl(false);
    const r = await claim();
    if (r?.ok) {
      if (pendingForRef.current === "timer") setScreen("timer");
      else if (pendingForRef.current === "alerts") setScreen("alerts");
      else enterScanner();
    }
    else console.error("[Interact] take-control claim failed:", r?.reason);
  };
  // Volume UI — mirrors the multiview control trio (mute / up / down).
  const [vol, setVolUi] = useState(ixVol);
  const [muted, setMutedUi] = useState(ixMuted);
  const volUp = () => {
    const n = Math.min(1, Math.round((ixVol + 0.1) * 10) / 10);
    setIxVol(n); setVolUi(n);
    if (ixMuted) { setIxMuted(false); setMutedUi(false); } // raising volume unmutes
  };
  const volDown = () => {
    const n = Math.max(0, Math.round((ixVol - 0.1) * 10) / 10);
    setIxVol(n); setVolUi(n);
  };
  const toggleMuteBtn = () => {
    const m = !ixMuted;
    setIxMuted(m); setMutedUi(m);
  };
    const cancelTakeControl = () => {
    setPendingControl(false);
    // No still lets them into the Timer (runs without band lights);
    // the scanner keeps requiring control — it's nothing but lights.
    if (pendingForRef.current === "timer") setScreen("timer");
  };
  // TIMER — same claim/handoff gating as the scanner. pendingFor remembers
  // which experience the take-control modal should open on Yes.
  const pendingForRef = useRef("scanner");
    const openTimer = async () => {
    if (isController) { setScreen("timer"); return; }
    if (hasHolder && holder?.holderDeviceId !== device?._id) {
      pendingForRef.current = "timer";
      setPendingControl(true);
      return;
    }
    const r = await claim();
    if (r?.ok) setScreen("timer");
    else console.error("[Interact] timer claim failed:", r?.reason);
  };

  // ALERTS — scanner rule: nothing but lights, so control is required to
  // enter. No on the modal does NOT open the screen.
  const openAlerts = async () => {
    if (isController) { setScreen("alerts"); return; }
    if (hasHolder && holder?.holderDeviceId !== device?._id) {
      pendingForRef.current = "alerts";
      setPendingControl(true);
      return;
    }
    const r = await claim();
    if (r?.ok) setScreen("alerts");
    else console.error("[Interact] alerts claim failed:", r?.reason);
  };

  const leaveScanner = () => {
    stopVoice();
    clearAll();
    sendCancel();
    setTimeout(() => sendCmd("SOLID_OFF", "OFF"), GAP_MS); // untracked on purpose: survives leaving the screen
    setArmed(false);
    setDormant(false);
    setPhase("standby");
    setScreen("menu");
  };

  const handleReaderTap = () => {
    if (asking) return; // answer the DVC question first
    if (phase === "standby" && dormant) {
      setDormant(false); // restarts the standby effect: CANCEL → wakeups → re-arm
      return;
    }
    if (phase !== "standby" || !armed) return;
    stopLoop();
    setPhase("welcome");
  };

  const handleFingerDown = (e) => {
    if (phase !== "prompt" || resultRef.current || windowExpiredRef.current) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setFingerDown(true);
    fingerDownRef.current = true;
    clearTimeout(holdTimerRef.current);
    holdTimerRef.current = setTimeout(() => settleResult("green"), HOLD_REQUIRED_MS);
    clearTimeout(longHoldTimerRef.current);
    setLongHold(false);
    longHoldTimerRef.current = setTimeout(() => setLongHold(true), KEEP_HOLDING_AFTER_MS);
  };

  const handleFingerUp = () => {
    clearTimeout(holdTimerRef.current);
    if (!resultRef.current) {
      clearTimeout(longHoldTimerRef.current);
      setLongHold(false);
    }
    setFingerDown(false);
    fingerDownRef.current = false;
    if (windowExpiredRef.current && !resultRef.current) settleResult("blue");
  };

  /* ===== Menu — built on DesignStudio's own classes so it matches step 1 ===== */
  if (screen === "menu") {
    return (
      <div className="ds-designer">
        <div className="ds-control-panel">
          <div className="ds-slide">
            <div className="ds-step-hero">
              <div className="ds-step-tag-row">
                <span className="ds-step-tag">INTERACT</span>
                <div className="ds-step-pills">
                  <span className="ds-step-pill active" />
                  <span className="ds-step-pill" />
                </div>
              </div>
              <h1 className="ds-step-title">Pick An Experience</h1>
            </div>
            <div className="ds-mode-buttons">
              <button className="ds-mode-btn" onClick={openScanner}>BAND SCANNER</button>
              <button className="ds-mode-btn" onClick={openTimer}>TIMER</button>
              <button className="ds-mode-btn" onClick={openAlerts}>ALERTS</button>
            </div>
            <div className="ds-actions">
              <button className="ds-btn-big ds-btn-back" onClick={onBack}>Back</button>
            </div>
          </div>
        </div>
                <div className="ds-band-stage ix-menu-stage">
                 <style>{IXV_STYLE}</style>
          <div className="ixv-controls">
            <button className={`ixv-btn ${muted ? "ixv-muted" : ""}`} onClick={toggleMuteBtn}><LuVolumeOff size={38} /></button>
            <button className="ixv-btn" onClick={volUp}><LuVolume2 size={38} /></button>
            <button className="ixv-btn" onClick={volDown}><LuVolume1 size={38} /></button>
          </div>
          <div className="ix-menu-cast">
            <div className="ix-speech ix-speech-enter">
              {lastName
                ? `Hi, ${lastName} Family! Pick an interactive experience to get started...`
                : "Hi there! Pick an interactive experience to get started..."}
            </div>
            <img className="ix-menu-cast-img" src={CAST_POINT_SRC} alt="" draggable="false" />
          </div>
        </div>

        {pendingControl && hasHolder && holder?.holderDeviceId !== device?._id && (
          <div className="beacon-handoff-backdrop">
            <div className="beacon-handoff-modal">
              <h3>Take Control of Your Magic Beacon?</h3>
              <p>
                Your Wait Time {holder.holderDeviceType}: {holder.holderLabel} is
                currently controlling the beacon. The Band Scanner needs control
                to light your bands. Switch control to this device?
              </p>
              <div className="beacon-handoff-buttons">
                <button className="beacon-handoff-no" onClick={cancelTakeControl}>
                  No
                </button>
                <button className="beacon-handoff-yes" onClick={confirmTakeControl}>
                  Yes, Switch
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
    /* ===== Timer ===== */
  if (screen === "timer") {
    return <InteractTimer onBack={() => setScreen("menu")} device={device} />;
  }
  /* ===== Alerts ===== */
  if (screen === "alerts") {
    return <InteractAlerts onBack={() => setScreen("menu")} device={device} />;
  }
  /* ===== Band Scanner ===== */
  const isGreen = phase === "green" || phase === "greenOut";
  const isBlue  = phase === "blue"  || phase === "blueOut";
  const isOut   = phase === "greenOut" || phase === "blueOut";
  const isColor = phase === "green" || phase === "blue";
  const ringColor = isGreen ? (dvc ? PURPLE : GREEN) : isBlue ? BLUE : WHITE;
  const colorTransition = isColor || isOut ? `opacity ${DISSOLVE_MS}ms ease` : "none";

  // Globe never goes solid white: it sweeps in every non-result phase.
  const globeSweep = !isColor && !isOut;
  const globeFastLap = phase === "scan" || phase === "ready" || phase === "prompt";
  const globeStyle = { color: ringColor, opacity: isColor ? 1 : 0, transition: colorTransition };

  const scannerFlash = phase === "prompt";
  const scannerStyle = {
    color: ringColor,
    opacity: isColor ? 1 : 0,
    transition: colorTransition,
    animationDuration: `${FLASH_CYCLE_MS}ms`,
  };

  // Pulsing tap target in the middle of the globe while waiting for the tap.
  const showTapPulse = phase === "standby" && armed && !dormant && !asking;

  /* Cast member: pose + line */
  const pose =
    isGreen ? "magical" :
    isBlue ? "sad" :
    (phase === "standby" && !armed && !dormant) ? "one" :
    "welcome";
  const hiLine = lastName
    ? `Hi, ${lastName} Family! I'll be with you in just one second...`
    : "Hi there! I'll be with you in just one second...";
  const line =
    phase === "standby"
      ? (dormant ? MSG_DORMANT : armed ? MSG_TAP : hiLine)
      : phase === "welcome" || phase === "scan" || phase === "ready" ? (pointerHint ? MSG_POINTER : MSG_HERE_WE_GO)
      : phase === "prompt"
        ? (fingerDown || resultRef.current
            ? (longHold ? MSG_KEEP : MSG_DETECTED)
            : nudge ? MSG_NUDGE : MSG_FINGER)
      : isGreen ? (dvc ? MSG_DVC_HOME : MSG_GREEN)
      : isBlue ? MSG_BLUE : "";

   return (
    <div className="ix-scanner">
      <style>{IXV_STYLE}</style>
      <div className="ixv-controls">
        <button className={`ixv-btn ${muted ? "ixv-muted" : ""}`} onClick={toggleMuteBtn}><LuVolumeOff size={38} /></button>
        <button className="ixv-btn" onClick={volUp}><LuVolume2 size={38} /></button>
        <button className="ixv-btn" onClick={volDown}><LuVolume1 size={38} /></button>
      </div>
      <button className="ix-back-btn ix-back-float" onClick={leaveScanner}>Back</button>
      <button className={`ix-dvc-pill ${dvc ? "on" : ""}`} onClick={toggleDvc}>
        <span>DVC</span>
        <span className="ix-switch"><span className="ix-knob" /></span>
      </button>

      {/* Cast member, pinned left */}
      <div className="ix-cast">
        {asking ? (
          <div className="ix-speech ix-speech-enter ix-ask">
            <div>{MSG_DVC_ASK}</div>
            <div className="ix-ask-buttons">
              <button className="ix-ask-btn" onClick={() => chooseDvc(true)}>YES</button>
              <button className="ix-ask-btn" onClick={() => chooseDvc(false)}>NO</button>
            </div>
          </div>
        ) : (
          <div className={`ix-speech ${isOut ? "ix-speech-out" : ""}`}>{line}</div>
        )}
        <div className="ix-cast-stack">
          <img className={`ix-cast-img ${pose === "welcome" ? "on" : ""}`} src={CAST_WELCOME_SRC} alt="" draggable="false" />
          <img className={`ix-cast-img ${pose === "magical" ? "on" : ""}`} src={CAST_MAGICAL_SRC} alt="" draggable="false" />
          <img className={`ix-cast-img ${pose === "sad" ? "on" : ""}`} src={CAST_SAD_SRC} alt="" draggable="false" />
          <img className={`ix-cast-img ${pose === "one" ? "on" : ""}`} src={CAST_ONE_SRC} alt="" draggable="false" />
        </div>
      </div>

      {/* Scanner art, shipped right */}
      <div
        className={`ix-stage-wrap ${fingerDown ? "ix-finger-down" : ""}`}
        onContextMenu={(e) => e.preventDefault()}
      >
        <img className="ix-img" src={BASE_SRC} alt="" draggable="false" />

        <svg className="ix-stage" viewBox="0 0 1280 800" aria-hidden="true">
          <defs>
            <clipPath id="ix-scanner-gap" clipPathUnits="userSpaceOnUse">
              <path clipRule="evenodd" d={SCANNER_GAP_CLIP} />
            </clipPath>
          </defs>

          {globeSweep ? (
            <g className="ix-ring-group">
              <GlobeLayers sweep lapMs={globeFastLap ? SCAN_LAP_MS : STANDBY_LAP_MS} />
            </g>
          ) : (
            <g className="ix-ring-group" style={globeStyle}>
              <GlobeLayers />
            </g>
          )}

          <g className={`ix-ring-group ${scannerFlash ? "ix-flashing" : ""}`} style={scannerStyle}>
            <ScannerLayers />
          </g>

          {/* Pulsing "press here" glow in the middle of the globe */}
          {showTapPulse && (
            <g className="ix-tap-pulse">
              <circle className="ix-tap-pulse-soft" cx={GLOBE.cx} cy={GLOBE.cy} r="92" />
              <circle className="ix-tap-pulse-core" cx={GLOBE.cx} cy={GLOBE.cy} r="34" />
            </g>
          )}

          {phase === "prompt" && (
            <g className="ix-arrow">
              <path className="ix-arrow-halo" style={{ stroke: dvc ? PURPLE : GREEN, opacity: 0.55 }} d={`M ${ARROW_POS.x - 30} ${ARROW_POS.y - 35} L ${ARROW_POS.x} ${ARROW_POS.y} L ${ARROW_POS.x + 30} ${ARROW_POS.y - 35}`} />
              <path className="ix-arrow-core" d={`M ${ARROW_POS.x - 30} ${ARROW_POS.y - 35} L ${ARROW_POS.x} ${ARROW_POS.y} L ${ARROW_POS.x + 30} ${ARROW_POS.y - 35}`} />
            </g>
          )}

          <circle
            className="ix-hit"
            cx={GLOBE.cx} cy={GLOBE.cy} r={GLOBE.rx + 40}
            onPointerDown={handleReaderTap}
          />
          <rect
            className="ix-hit"
            x={SCANNER.cx - 150} y={SCANNER.cy - 80} width={300} height={190}
            onPointerDown={handleFingerDown}
            onPointerUp={handleFingerUp}
            onPointerCancel={handleFingerUp}
          />
        </svg>
      </div>
    </div>
  );
};

export default Interact;
